// Admin page of one elimination bracket, single or double (season → Brackets tab).
// Draft: seeding next to a live preview. Started: the bracket + a side panel per match.
// `embedded` = the playoffs tab of a two-stage bracket (StagePage): no back / delete / public
// link / finish / cancel - those belong to the stage.
import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { AlertModal, ConfirmModal } from '../AlertModal';
import EliminationView from './EliminationView';
import SeedingEditor from './SeedingEditor';
import MatchPanel from './MatchPanel';
import { roundLabel } from './bracketText';
import './bracket.css';

export default function EliminationPage({ bracketData, servers, allTeams, onBack, onReload, onDelete, embedded }) {
  const { t } = useTranslation();
  const { bracket, matches = [], teams = {} } = bracketData;
  const [selId, setSelId] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'info' });
  const [confirm, setConfirm] = useState({ show: false, message: '', onConfirm: null });
  const [busy, setBusy] = useState(false);

  const status = bracket.status;
  const draft = status === 'draft';
  const double = bracket.bracket_type === 'double_elimination';
  const gfMode = bracket.grand_final_mode || (bracket.grand_final_reset ? 'reset' : 'simple');
  const rounds = bracket.rounds || matches.reduce((mx, m) => (m.is_third_place || (m.bracket_side || 'winners') !== 'winners' ? mx : Math.max(mx, m.round_number)), 0);
  const third = matches.find(m => m.is_third_place);
  const selected = selId ? matches.find(m => m.id === selId) : null;
  const errText = (e) => (e.response && e.response.data && e.response.data.error) || e.message;

  const put = async (body) => {
    setBusy(true);
    try { await axios.put(`/api/brackets/${bracket.id}`, body); await onReload(); }
    catch (e) { setAlert({ show: true, message: errText(e), type: 'error' }); }
    finally { setBusy(false); }
  };
  const canSwap = status === 'draft' || status === 'active';
  const swap = async (a, b) => {
    try { await axios.put(`/api/brackets/${bracket.id}/slots/swap`, { a, b }); await onReload(); }
    catch (e) { setAlert({ show: true, message: errText(e), type: 'error' }); }
  };
  const ask = (message, fn) => setConfirm({ show: true, message, onConfirm: async () => { setConfirm({ show: false }); await fn(); } });

  const resetBracket = () => ask(t('bracket.confirm.reset', 'Reset the whole bracket to draft? Every result is cleared. Panel matches already created stay, but no longer belong to the bracket.'), async () => {
    setBusy(true);
    try {
      const res = await axios.put(`/api/brackets/${bracket.id}/reset`);
      await onReload();
      const ids = res.data.unlinked || [];
      setAlert({ show: true, type: 'success', message: ids.length
        ? t('bracket.resetUnlinked', 'Bracket reset to draft. Matches #{{ids}} were unlinked.', { ids: ids.join(', #') })
        : t('bracket.resetDone', 'Bracket reset to draft.') });
    } catch (e) { setAlert({ show: true, message: errText(e), type: 'error' }); }
    finally { setBusy(false); }
  });

  // best-of per round. Virtual rounds = the server's (stage_formats): upper / single r,
  // lower 200 + r, grand final 300, third place rounds + 100.
  const side = (m) => m.bracket_side || 'winners';
  const entries = [];
  const addEntry = (list, vr) => { const m = list[0]; if (m) entries.push({ vr, m, bo: Number(m.best_of || 1) }); };
  for (let r = 1; r <= rounds; r++) addEntry(matches.filter(x => side(x) === 'winners' && !x.is_third_place && x.round_number === r), r);
  if (double) {
    const lbR = matches.reduce((mx, m) => (side(m) === 'losers' ? Math.max(mx, m.round_number) : mx), 0);
    for (let r = 1; r <= lbR; r++) addEntry(matches.filter(x => side(x) === 'losers' && x.round_number === r), 200 + r);
    addEntry(matches.filter(x => side(x) === 'grand_final'), 300);
  } else if (third) addEntry([third], rounds + 100);
  const setBo = (vr, bo) => put({ stage_formats: entries.map(e => ({ from_round: e.vr, to_round: e.vr, best_of: e.vr === vr ? bo : e.bo })) });

  const counts = matches.reduce((c, m) => {
    if (m.status === 'completed' && !m.walkover) c.done++;
    if (m.live || m.match_status === 'live') c.live++;
    if (!m.walkover) c.total++;
    return c;
  }, { done: 0, live: 0, total: 0 });

  return (
    <div className="br-page">
      <AlertModal show={alert.show} message={alert.message} type={alert.type} onClose={() => setAlert({ ...alert, show: false })} />
      <ConfirmModal show={confirm.show} message={confirm.message} onConfirm={confirm.onConfirm} onClose={() => setConfirm({ show: false })} />

      <div className="br-head">
        {!embedded && <button className="br-btn ghost" onClick={onBack}>← {t('common.back', 'Back')}</button>}
        <div>
          {embedded ? <h3 style={{ margin: 0 }}>{t('bracket.po.title', 'Playoffs')}</h3> : <h2>{bracket.name}</h2>}
          <div className="br-sub">
            {double ? t('bracket.type.double', 'Double elimination') : t('bracket.singleElim', 'Single elimination')} · {t('bracket.teamsN', '{{n}} teams', { n: bracket.team_count })}
            {!draft && <> · {t('bracket.playedOf', '{{done}}/{{total}} played', counts)}</>}
            {counts.live > 0 && <> · <span className="st-live">{t('bracket.liveN', '{{n}} live', { n: counts.live })}</span></>}
          </div>
        </div>
        <span className={`br-pill ${status}`}>{t(`bracketStatus.${status}`, status)}</span>
        <div className="br-spacer" />
        <div className="br-actions">
          {!embedded && <a className="br-btn ghost" href={`/bracket/${bracket.id}`} target="_blank" rel="noreferrer">{t('bracket.publicPage', 'Public page')} ↗</a>}
          {draft && <button className="br-btn green" disabled={busy || matches.length === 0}
            onClick={() => ask(embedded ? t('bracket.po.confirmStart', 'Start the playoffs? Seeding is locked once they start, and the stage results can no longer change.')
              : t('bracket.confirm.start', 'Start the bracket? Seeding is locked once it starts.'), () => put({ status: 'active' }))}>▶ {embedded ? t('bracket.po.start', 'Start playoffs') : t('bracket.start', 'Start')}</button>}
          {!embedded && (status === 'active' || status === 'completed') && <button className="br-btn ghost" disabled={busy}
            onClick={() => ask(t('bracket.confirm.finish', 'Mark the bracket as finished?'), () => put({ status: 'finished' }))}>{t('bracket.finish', 'Finish')}</button>}
          {!embedded && status === 'active' && <button className="br-btn ghost" disabled={busy}
            onClick={() => ask(t('bracket.confirm.cancel', 'Cancel this bracket?'), () => put({ status: 'cancelled' }))}>{t('common.cancel', 'Cancel')}</button>}
          {!draft && <button className="br-btn amber" disabled={busy} onClick={resetBracket}>{embedded ? t('bracket.po.reset', 'Playoffs back to draft') : t('bracket.reset', 'Reset to draft')}</button>}
          {!embedded && <button className="br-btn danger" disabled={busy} onClick={() => onDelete(bracket.id)}>{t('common.delete', 'Delete')}</button>}
        </div>
      </div>

      <div className="br-card" style={{ padding: '0.6rem 0.8rem' }}>
        <div className="br-formats">
          <span className="br-hint" style={{ fontWeight: 700 }}>{t('bracket.formatPerRound', 'Format per round')}</span>
          {entries.map(e => (
            <label key={e.vr} className="br-format">
              {e.vr === 300 ? t('bracket.round.grandFinal', 'Grand final') : roundLabel(t, e.m.round_name, e.m.round_number)}
              <select value={e.bo} disabled={busy || status === 'finished' || status === 'cancelled'} onChange={ev => setBo(e.vr, Number(ev.target.value))}>
                {[1, 3, 5].map(b => <option key={b} value={b}>BO{b}</option>)}
              </select>
            </label>
          ))}
          {double ? (
            <span className="br-format" title={draft ? '' : t('bracket.thirdDraftOnly', 'Only in a draft')}>
              {t('bracket.gf.label', 'Grand final')}
              <select value={gfMode} disabled={busy || !draft} onChange={e => put({ grand_final_mode: e.target.value })}>
                <option value="simple">{t('bracket.gf.simple', 'One match')}</option>
                <option value="reset">{t('bracket.gf.reset', 'With reset')}</option>
                <option value="none">{t('bracket.gf.none', 'None - upper final decides')}</option>
              </select>
            </span>
          ) : (
            <span className="br-format" title={draft ? '' : t('bracket.thirdDraftOnly', 'Only in a draft')}>
              {t('bracket.thirdPlaceMatch', '3rd place match')}
              <button type="button" className={`br-toggle${bracket.has_third_place ? ' on' : ''}`} disabled={busy || !draft || bracket.team_count < 3}
                onClick={() => put({ has_third_place: !bracket.has_third_place })}><span /></button>
            </span>
          )}
        </div>
        {!draft && <div className="br-hint" style={{ marginTop: '0.4rem' }}>{t('bracket.formatHint', 'Changes apply to matches not played yet.')}</div>}
      </div>

      {draft ? (
        <div className="br-draft">
          <SeedingEditor bracket={bracket} allTeams={allTeams} onChanged={onReload} />
          <div>
            <div className="br-hint" style={{ marginBottom: '0.4rem' }}>{embedded
              ? t('bracket.po.draftHint', 'The stage is finished and the playoffs are seeded from its standings. Check the seeding, then start the playoffs.')
              : t('bracket.previewHint', 'Preview - results can already be set, matches are created after Start.')}</div>
            <EliminationView bracket={bracket} matches={matches} teams={teams} selectedId={selId} onSelect={m => setSelId(m.id)} onSwap={canSwap ? swap : undefined} />
          </div>
        </div>
      ) : (
        <>
          <EliminationView bracket={bracket} matches={matches} teams={teams} selectedId={selId} onSelect={m => setSelId(m.id)} onSwap={canSwap ? swap : undefined} />
          <div className="br-legend">
            <span><i style={{ background: 'var(--accent-cyan)' }} />{t('bracket.status.ready', 'Ready')}</span>
            <span><i style={{ background: 'var(--accent-blue)' }} />{t('bracket.status.scheduled', 'Scheduled')}</span>
            <span><i style={{ background: 'var(--accent-red)' }} />{t('bracket.status.live', 'Live')}</span>
            <span><i style={{ background: 'var(--accent-amber)' }} />{t('bracket.status.decision', 'Needs decision')}</span>
            <span><i style={{ background: 'var(--accent-green)' }} />{t('bracket.status.done', 'Done')}</span>
            <span>{t('bracket.clickHint', 'Click a match to create it on a server or set the result.')} {t('bracket.dragHint2', 'Drag a team onto another team to change the matchups.')}</span>
          </div>
        </>
      )}

      {selected && (
        <MatchPanel bracket={bracket} match={selected} matches={matches} teams={teams} servers={servers}
          onClose={() => setSelId(null)} onChanged={onReload} />
      )}
    </div>
  );
}
